import { useCallback, useEffect, useRef } from 'react';
import { Logout } from '@/apis/auth';
import { flushActivityBeforeLogout } from '@/hooks/useActivityBeacon/useActivityBeacon';
import { useToken } from '@/common/helper';
import { clearAllStorageAndState } from '@/common/helper/storageCleanup';
import { useWebSocket } from '@/context/WebSocketContext';

// Cencora/World Courier's white-label domain only. Everywhere else this
// hook attaches zero listeners and does nothing.
const IDLE_LOGOUT_HOSTNAME = 'worldcourier.zoomlogi.com';
const IDLE_TIMEOUT_MS = 10 * 60 * 1000;
const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
] as const;

/**
 * Logs the user out after IDLE_TIMEOUT_MS of no mouse/keyboard/touch/scroll
 * activity, scoped to Cencora's white-label domain. Mirrors the manual
 * logout flow in ResponsiveHeader.tsx (Logout() API call to blacklist the
 * refresh token server-side, then full client-state cleanup) rather than
 * clearing local storage alone.
 */
const useIdleLogout = (isAuthenticated: boolean): void => {
  const { removeToken, setUserId } = useToken();
  const { setSelectedUserID } = useWebSocket() || {};
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearAllStates = useCallback(() => {
    removeToken();
    setUserId(null);
    setSelectedUserID?.(null);
    clearAllStorageAndState({
      broadcast: true,
      navigateToLogin: true,
      purgePersisted: true,
    }).catch(console.error);
  }, [removeToken, setUserId, setSelectedUserID]);

  const logoutForIdle = useCallback(async () => {
    try {
      await flushActivityBeforeLogout();
      await Logout();
    } catch (err) {
      console.error(err);
    } finally {
      clearAllStates();
    }
  }, [clearAllStates]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (window.location.hostname !== IDLE_LOGOUT_HOSTNAME) return undefined;
    if (!isAuthenticated) return undefined;

    const resetTimer = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(logoutForIdle, IDLE_TIMEOUT_MS);
    };

    resetTimer();
    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, resetTimer, { passive: true })
    );

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, resetTimer)
      );
    };
  }, [isAuthenticated, logoutForIdle]);
};

export default useIdleLogout;
